import { Box, Stack, type Theme, useMediaQuery } from '@mui/material';
import Button from '@mui/material/Button';
import { debounce } from 'lodash';
import { enqueueSnackbar } from 'notistack';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import Header from '/@/components/bayer/header';
import PageContainer from '/@/components/bayer/page-container';
import BrandDisplay from '/@/components/brand-display';
import FieldInfo from '/@/components/field-info';
import FrostRiskReco from '/@/components/frost-risk-reco';
import ModuleLayout from '/@/components/module-layout';
import RecommendationCommentsButton from '/@/components/recommendation-comments-button';
import RecommendationCommentsDrawer from '/@/components/recommendation-comments-drawer';
import RecommendationShare from '/@/components/recommendation-share';
import Spinner from '/@/components/spinner';
import settings from '/@/core/settings';
import { useBreadcrumbs } from '/@/hooks/breadcrumbs';
import { useFieldById } from '/@/hooks/fields';
import { useDefaultCropConfig, useRecommendationCommentsPermissions } from '/@/hooks/recommendation';
import { _ } from '/@/locale';
import { setField as setCMField, setRecommendation as setCMRecommendation } from '/@/store/crop-monitoring/actions';
import {
    createRecommendationFromAPI,
    updateRecommendationFromAPI,
} from '/@/store/private-data/recommendations/apiProviders';
import { getCropConfig, getShowPlantingDateWarning } from '/@/store/private-data/recommendations/selectors';
import { updateActiveRecommendation, updateActiveRecommendationCropConfig } from '/@/store/recommendations/actions';
import { getActiveRecommendation } from '/@/store/recommendations/selectors';
import { getAppInstance, getSessionBrands, getSessionCountry } from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { formatDate, isMobile } from '/@/utils';
import { reverse } from '/@/utils/router';
import { getCrops } from '../../dashboard/hooks/crops';
import getInformationModules from '../recommendation-detail/modules';
import CropConfiguration from '../recommendation-edit/modules/crop-configuration';
import PestsConfiguration from '../recommendation-edit/modules/pests-configuration';
import ProductSelection from '../recommendation-edit/modules/product-selection';
import { useRecommendationEditData, useRecommendationResultData } from './hooks';

type Props = {
    crop: 'corn' | 'soy';
    fieldId: string;
};

const DefaultRecoWrapper = (Component: React.ComponentType<Props>) => {
    return function DefaultRecoWrapperInner(props: Props) {
        const dispatch = useDispatch();
        const { data: field } = useFieldById({ id: Number(props.fieldId) });
        const appInstance = useSelector(getAppInstance);
        const defaultCropConfig = useDefaultCropConfig({ crop: props.crop, field });

        useEffect(() => {
            return () => {
                dispatch(updateActiveRecommendation(undefined));
            };
        }, []);

        useEffect(() => {
            if (!field) return;
            dispatch(
                updateActiveRecommendation({
                    appInstance: appInstance.id,
                    field: props.fieldId,
                    crops: [defaultCropConfig],
                })
            );
        }, [appInstance.id, dispatch, field, props.crop, props.fieldId]);

        const recommendation = useSelector(getActiveRecommendation);

        if (recommendation) {
            return <Component {...props} />;
        }
        return (
            <Box display='flex' justifyContent='center' alignItems='center' sx={{ height: '100%', width: '100%' }}>
                <Spinner />
            </Box>
        );
    };
};

// AIO=AllInOne (Edit + Detail): Development name
const RecommendationAIO = ({ crop, fieldId }: Props) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const queryParams = new URLSearchParams(window.location.search);

    const [newRecommendation, setNewRecommendation] = useState<unknown | undefined>(undefined); // Workarround to download PDF
    const [commentsDrawerOpen, setCommentsDrawerOpen] = useState(false);
    const recommendationCommentsStatus = (
        newRecommendation as { commentsStatus?: 'no_comments' | 'has_unread_comments' | 'has_comments' }
    )?.commentsStatus;
    const { shouldShowCommentsButton } = useRecommendationCommentsPermissions(
        recommendationCommentsStatus,
        newRecommendation?.userNode
    );
    const { data: field } = useFieldById({ id: fieldId });
    const appInstance = useSelector(getAppInstance);
    const recommendation = useSelector(getActiveRecommendation);
    const sessionBrands = useSelector(getSessionBrands);
    const cropConfig = getCropConfig(recommendation, crop);

    const brand = cropConfig ? sessionBrands[cropConfig.crop][0] : {}; // TODO: BRAND
    const country = useSelector(getSessionCountry);

    const handleChange = (key) => {
        return async (value) => {
            if (['plantingDate', 'environment'].includes(key)) {
                await dispatch(
                    updateActiveRecommendationCropConfig({
                        key: 'products',
                        value: [],
                    })
                );
            }
            await dispatch(
                updateActiveRecommendationCropConfig({
                    key,
                    value,
                })
            );
        };
    };

    //////////////////////////// Edit Data
    const {
        soilMoisturesQuery,
        soilTypesQuery,
        environmentsQuery,
        productsQuery,
        mappedProducts,
        isLoading,
        handleProductToggle,
        validPlantingDates,
        climatePatternChoices,
        pestChoices,
        metricChoices,
        featuredProducts,
        otherProducts,
    } = useRecommendationEditData({
        field,
        crop,
        handleChange,
    });

    const showPlantingDateWarning = getShowPlantingDateWarning(featuredProducts, cropConfig);

    //////////////////////////// Reco Data

    const {
        metricsQuery,
        featuresQuery,
        soyPlantingRecommendationQuery,
        cornDensityCurveQuery,
        fertilizationCoefficientsQuery,
        pestControlPlans,
        catalogBrands,
        productsWithAPIData,
    } = useRecommendationResultData({
        field,
        crop,
        cropConfig,
        mappedProducts,
    });

    ////////////////////////////

    const [searchParams, setSearchParams] = useSearchParams();
    useEffect(() => {
        mpTrackEvent('Seed recommendation began', {
            crop,
            entry_source: searchParams.get('entrySource'),
        });
    }, []);

    const isValid = () => {
        if (!cropConfig) return false;
        return recommendation.crops[0]['products'].length > 0;
    };
    const isSaving = useRef(false);

    const breadcrumbs = useBreadcrumbs('recommendation.RecommendationDetail');

    const handleSaveRecommendation = useCallback(
        async ({
            recommendation,
            productsWithAPIData,
            brand,
            crop,
            pestControlPlans,
            newRecommendation,
            field,
            catalogBrands,
            cropConfig,
        }) => {
            try {
                if (isSaving.current) return;
                isSaving.current = true;
                const body = {
                    ...recommendation,
                    crops: [
                        {
                            ...recommendation.crops[0],
                            [`${crop}Products`]: recommendation.crops[0].products,
                            pestControlPlans: Object.values(cropConfig.pestControlPlans).map((el) => el.controlPlanId),
                        },
                    ],
                    status: 'read_only',
                    backup: {
                        ...recommendation,
                        status: 'read_only',
                        crops: [
                            {
                                ...recommendation?.crops?.[0],
                                products: recommendation?.crops?.[0]?.[`${crop}Products`],
                                productRecommendations: productsWithAPIData,
                                pestControlPlans: pestControlPlans,
                                catalogBrands,
                            },
                        ],
                        weeds: [],
                        field,
                        brand,
                    },
                };

                let newReco;
                if (newRecommendation) {
                    newReco = await updateRecommendationFromAPI({ ...body, id: newRecommendation.id });
                    mpTrackEvent('Seed recommendation updated', {
                        crop,
                        entry_source: 'recommendation-detail',
                        number_of_hybrids: recommendation?.crops?.[0]?.products?.length,
                        id: newReco.id,
                    });
                } else {
                    newReco = await createRecommendationFromAPI(body);
                    mpTrackEvent('Seed recommendation saved', {
                        crop,
                        entry_source: 'recommendation-detail',
                    });
                    dispatch(trackEvent({ key: `recomendation.create.${crop}` }));
                    enqueueSnackbar(
                        _('¡Recomendación guardada con éxito! Podés consultarla en la sección “Mis recomendaciones”.'),
                        {
                            variant: 'success',
                            SnackbarProps: {
                                'data-testid': 'recommendation-saved-snackbar',
                            },
                        }
                    );
                }

                setNewRecommendation(newReco);
                if (queryParams.get('from') === 'cropMonitoring') {
                    dispatch(setCMField(field.id));
                    dispatch(setCMRecommendation(newReco.id));
                    navigate(reverse('cropMonitoring.CropMonitoringCorn'));
                }
            } catch (error) {
                console.error(error);
            } finally {
                isSaving.current = false;
            }
        },
        []
    );

    const debouncedHandleSaveRecommendation = useCallback(debounce(handleSaveRecommendation, 1000), []);

    useEffect(() => {
        if (queryParams.get('from') !== 'cropMonitoring' && isValid()) {
            debouncedHandleSaveRecommendation({
                recommendation,
                productsWithAPIData,
                brand,
                crop,
                pestControlPlans,
                newRecommendation,
                field,
                catalogBrands,
                cropConfig,
            });
        }
    }, [recommendation, productsWithAPIData]);

    const getModules = () => {
        if (!cropConfig) return [];
        const modules = [
            {
                menuLabel: CropConfiguration.menuLabel(),
                component: (
                    <CropConfiguration
                        field={field}
                        recommendation={recommendation}
                        cropConfig={cropConfig}
                        products={featuredProducts}
                        environment={
                            environmentsQuery.data || {
                                percentiles: [],
                                min: 3000,
                                max: 15000,
                                default: 9000,
                            }
                        }
                        soilTypeChoices={soilTypesQuery.data?.soilTypesChoices || []}
                        irrigationChoices={soilTypesQuery.data?.irrigationChoices || []}
                        validPlantingDates={validPlantingDates}
                        showPlantingDateWarning={showPlantingDateWarning}
                        soilMoistureChoices={soilMoisturesQuery.data || []}
                        climatePatternChoices={climatePatternChoices}
                        setPlantingDate={handleChange('plantingDate')}
                        setClimatePattern={handleChange('climatePattern')}
                        setSoilMoisture={handleChange('soilMoisture')}
                        setSoilType={handleChange('soilType')}
                        setIrrigation={handleChange('irrigation')}
                        setEnvironment={handleChange('environment')}
                        loading={isLoading}
                    />
                ),
            },
            {
                menuLabel: ProductSelection.menuLabel(),
                component: (
                    <ProductSelection
                        brand={sessionBrands[cropConfig.crop][0]} // TODO: BRAND
                        cropConfig={cropConfig}
                        featuredProducts={featuredProducts}
                        otherProducts={otherProducts}
                        metricChoices={metricChoices}
                        onToggle={handleProductToggle}
                        onMetricChange={handleChange(`${crop}Metric`)}
                        country={field?.country?.id}
                        loading={isLoading}
                        recommendation={recommendation}
                    />
                ),
            },
        ];

        if (!!pestChoices?.length && settings.app.theme !== 'expertobayer') {
            modules.push({
                menuLabel: PestsConfiguration.menuLabel(),
                component: (
                    <PestsConfiguration
                        cropConfig={cropConfig}
                        pestChoices={pestChoices}
                        selectedControlPlans={recommendation.crops?.[0]?.pestControlPlans}
                        handleChange={handleChange('pestControlPlans')}
                        recommendation={recommendation}
                    />
                ),
            });
        }

        if (isValid())
            modules.push(
                ...getInformationModules({
                    appInstance,
                    recommendation,
                    cropConfig,
                    productRecommendations: productsWithAPIData,
                    pestControlPlans,
                    brand,
                    country,
                    catalogBrands,
                    isPdf: false,
                }).map((module, i) => ({ ...module, expandable: true }))
            );

        return modules;
    };

    const isSmOrLower = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    if (!recommendation)
        return (
            <Box display='flex' justifyContent='center' alignItems='center' sx={{ height: '100%', width: '100%' }}>
                <Spinner />
            </Box>
        );

    return (
        <PageContainer
            heroHeader={
                <Header
                    variant='full-gray'
                    title={_('Recomendación de híbrido de {{crop}}', {
                        crop: getCrops()[cropConfig.crop]?.name,
                    })}
                    subtitle={`${_('Productos sugeridos según el ambiente del lote y el rendimiento objetivo.')} ${
                        newRecommendation?.updated
                            ? _('Última actualización {{date}} {{time}}', {
                                  date: formatDate(new Date(newRecommendation?.updated)),
                                  time: new Date(newRecommendation?.updated).toLocaleTimeString(),
                              })
                            : ''
                    }`}
                    actions={[
                        <RecommendationShare
                            key='recommendation-share'
                            type={cropConfig.crop}
                            recommendation={{ ...newRecommendation, ...newRecommendation?.backup }}
                            disabled={!newRecommendation}
                            version={2}
                        />,
                        newRecommendation?.id &&
                            shouldShowCommentsButton &&
                            (isSmOrLower ? (
                                <Button
                                    variant='contained'
                                    fullWidth
                                    color='primary'
                                    onClick={() => setCommentsDrawerOpen(true)}
                                >
                                    {_('Ver comentarios')}
                                </Button>
                            ) : (
                                <RecommendationCommentsButton
                                    key='comments-button'
                                    commentsStatus={newRecommendation.commentsStatus}
                                    onOpenDrawer={() => setCommentsDrawerOpen(true)}
                                />
                            )),
                    ].filter(Boolean)}
                    breadcrumbs={breadcrumbs}
                />
            }
        >
            <ModuleLayout
                sidebar={
                    <Fragment>
                        <BrandDisplay brand={brand} borderRadius={false} />
                        <FieldInfo showCompleteInfo shouldCollapse={isMobile()} field={field} />
                        {appInstance.enableFrostRisk && <FrostRiskReco />}
                    </Fragment>
                }
                modules={getModules()}
                actions={
                    <Stack direction='row' justifyContent='flex-end' alignItems='center' spacing={2}>
                        {queryParams.get('from') === 'cropMonitoring' && (
                            <Button
                                variant='outlined'
                                disabled={!isValid() || recommendation.status === 'read_only'}
                                onClick={() =>
                                    handleSaveRecommendation({
                                        recommendation,
                                        productsWithAPIData,
                                        brand,
                                        crop,
                                        pestControlPlans,
                                        newRecommendation,
                                        field,
                                        catalogBrands,
                                        cropConfig,
                                    })
                                }
                            >
                                {_('Guardar')}
                            </Button>
                        )}
                    </Stack>
                }
            />
            {newRecommendation?.id && (
                <RecommendationCommentsDrawer
                    open={commentsDrawerOpen}
                    setCommentsDrawerOpen={setCommentsDrawerOpen}
                    recommendationId={newRecommendation.id}
                />
            )}
        </PageContainer>
    );
};

const WrappedRecommendationAIO = DefaultRecoWrapper(RecommendationAIO);
export default WrappedRecommendationAIO;
